// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.

import React from 'react';
import waveImage from '../assets/images/waveImg.svg';

export default function CircleSpinner(): JSX.Element {
  /** Outer circles */
  const mainCircle4: React.CSSProperties = {
    position: 'absolute',
    width: '124px',
    height: '124px',
    borderRadius: '50%',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    background: '#00D2AA30',
    animation: 'pulse 1s ease-out infinite'
  };

  const mainCircle3: React.CSSProperties = {
    position: 'absolute',
    width: '114px',
    height: '114px',
    borderRadius: '50%',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    background: '#00D2AA40',
    animation: 'pulse 1s ease-out infinite'
  };

  const mainCircle2: React.CSSProperties = {
    position: 'absolute',
    width: '104px',
    height: '104px',
    borderRadius: '50%',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    background: '#00D2AA50',
    animation: 'pulse 1s ease-out infinite'
  };

  const mainCircle1: React.CSSProperties = {
    position: 'absolute',
    width: '94px',
    height: '94px',
    borderRadius: '50%',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    background: '#00D2AA60',
    animation: 'pulse 1s ease-out infinite'
  };

  const mainCircle: React.CSSProperties = {
    position: 'absolute',
    width: '84px',
    height: '84px',
    borderRadius: '50%',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    background: '#00D2AA80',
    animation: 'pulse 1s ease-out infinite'
  };

  /** Core circle */
  const core: React.CSSProperties = {
    position: 'absolute',
    width: 50,
    height: 50,
    borderRadius: '50%',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    background: '#fff',
    border: '1px solid #0000000F',
    zIndex: 3
  };

  /** Rotating inner ring */
  const rotatingInner: React.CSSProperties = {
    position: 'absolute',
    width: 74,
    height: 74,
    borderRadius: '50%',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    background: '#ffffff',
    border: '1px solid #eee',
    animation: 'rotateWhite 2s linear infinite',
    zIndex: 4
  };

  /** Orbit container */
  const orbitContainer: React.CSSProperties = {
    position: 'absolute',
    width: 8,
    height: 30,
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    animation: 'spinInner 1.5s linear infinite',
    zIndex: 6
  };

  const innerCircleBase: React.CSSProperties = {
    position: 'absolute',
    width: 20,
    height: 20,
    borderRadius: '50%'
  };

  const leftInner: React.CSSProperties = {
    ...innerCircleBase,
    background: '#1570EF',
    left: 0,
    top: '50%',
    transform: 'translateY(-50%)'
  };

  const rightInner: React.CSSProperties = {
    ...innerCircleBase,
    background: '#2E90FA',
    right: 0,
    top: '50%',
    transform: 'translateY(-50%)',
    opacity: 0.9
  };

  const svgLineStyle = (zIndex: number): React.CSSProperties => ({
    backgroundImage: `url(${waveImage})`,
    backgroundSize: 'cover',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center',
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-45%, -50%)',
    width: 750,
    height: 212,
    overflow: 'visible',
    zIndex,
    mixBlendMode: 'multiply',
    opacity: '0.7',
    animation: 'waveSlide 6s linear infinite'
  });

  return (
    <div>
      <style>
        {`
          @keyframes spinInner {
            from { transform: translate(-50%, -50%) rotate(0deg); }
            to   { transform: translate(-50%, -50%) rotate(360deg); }
          }

          @keyframes rotateWhite {
            0% { transform: translate(-50%, -50%) rotate(0deg); }
            100% { transform: translate(-50%, -50%) rotate(360deg); }
          }

          @keyframes pulse {
            0% {
              transform: translate(-50%, -50%) scale(1);
              opacity: 1;
            }
            100% {
              transform: translate(-50%, -50%) scale(1.01);
              opacity: 1;
            }
          }

          @keyframes waveSlide {
            0% { transform: translate(-50%, -50%) translateY(0) rotateX(0deg); }
            25% { transform: translate(-50%, -50%) translateY(0) rotateX(30deg); }
            50% { transform: translate(-50%, -50%) translateY(0) rotateX(0deg); }
            75% { transform: translate(-50%, -50%) translateY(0) rotateX(30deg); }
            100% { transform: translate(-50%, -50%) translateY(0) rotateX(0deg); }
          }
        `}
      </style>
      <div style={mainCircle4}>
        <div style={mainCircle3}>
          <div style={mainCircle2}>
            <div style={mainCircle1}>
              <div style={mainCircle}>
                <div style={core}></div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div style={rotatingInner}></div>
      <div style={orbitContainer}>
        <div style={leftInner}></div>
        <div style={rightInner}></div>
      </div>
      <div style={svgLineStyle(1)} />
    </div>
  );
}
